import { ChangeDetectionStrategy, Component, effect, input, signal, inject } from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { toCanvas } from 'qrcode';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-qr-code-generator',
    imports: [NgClass, MatButtonModule, NgOptimizedImage],
    template: `
    <div class="qr-container" [ngClass]="{ loading: isGenerating() }">
      <div class="qr-wrapper">
        <div [innerHTML]="qrCodeHtml()"></div>
        @if (showLogo()) {
        <div class="logo-overlay">
          <img
            [ngSrc]="'./shared-assets/icons/awo-logo.svg'"
            [alt]="'AWO Logo'"
            [width]="40"
            [height]="32"
            priority="low"
          />
        </div>
        }
      </div>
      @if (isGenerating()) {
      <div class="loader">Generating...</div>
      }
    </div>
  `,
    styles: [
        `
      .qr-container {
        padding: 16px;
        position: relative;
      }
      .qr-wrapper {
        position: relative;
        display: inline-block;
      }
      .logo-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        padding: 4px;
        width: 15%;
        aspect-ratio: 1.25/1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .loading {
        opacity: 0.5;
      }
      .loader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QrCodeGeneratorComponent {
  private sanitizer = inject(DomSanitizer);

  inputString = input.required<string>();
  showLogo = input(true);
  isGenerating = signal(false);
  qrCodeHtml = signal<SafeHtml>('');

  constructor() {
    effect(() => {
      const value = this.inputString();
      if (value) {
        this.generateQRCode(value);
      }
    });
  }

  private async generateQRCode(value: string): Promise<void> {
    if (!value) return;
    this.isGenerating.set(true);

    try {
      const canvas = document.createElement('canvas');
      await toCanvas(canvas, value, {
        errorCorrectionLevel: 'H',
        margin: 2,
        scale: 8,
        width: 300,
      });

      const qrCodeSvg = canvas.toDataURL('image/svg+xml');
      this.qrCodeHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(
          `<img src="${qrCodeSvg}" alt="QR Code" style="max-width: 100%; height: auto;">`
        )
      );
    } catch (error: unknown) {
      console.error('Failed to generate QR code:', error);
    } finally {
      this.isGenerating.set(false);
    }
  }
}
